import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TextField,
  TableBody,
  TablePagination,
  IconButton,
  Menu,
  Tooltip,
} from "@mui/material";

import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  CheckCircleOutline as FinalizeIcon,
} from "@mui/icons-material";
import { useClientInfo, axiosInstance } from "../../utils/commonImports";
import TripsheetFormAdd from "./TripsheetFormAdd"; // Your Add/Edit form
import { useConfirmation } from "../../hooks/useConfirmation";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MyDatePicker from "../../components/MyDatePicker";
import PreviewModal from "../../components/preview/PreviewModal";
import { useAppContext } from "../../context/AppContext";

dayjs.extend(customParseFormat);

const STATUS_FILTERS = [
  "All",
  "Non Closed",
  "Closed by Driver",
  "Billed",
  "Non Billed",
  "Cancelled",
];

// "Closed by Driver" is filtered in the browser (the API does not know it)
const CLIENT_SIDE_STATUS = "Closed by Driver";

export default function TripsheetForm() {
    const { clientId } = useClientInfo();
  const { fyid } = useAppContext();

  const tableRef = useRef();

  // Data states
  const [tripDuties, setTripDuties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vehicleGroups, setVehicleGroups] = useState([]);

  // Filters states
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterTripsheetNumber, setFilterTripsheetNumber] = useState("");
  const [filterFromDate, setFilterFromDate] = useState(null);
  const [filterToDate, setFilterToDate] = useState(null);
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterVendor, setFilterVendor] = useState("");
  const [filterVehicleGroup, setFilterVehicleGroup] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(13);

  // Edit/Add states
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);

  // Menu and Snackbar
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [printingId, setPrintingId] = useState(null); // tripsheetid currently generating a preview

  // Print preview — reuses the same PreviewModal component TripsheetFormAdd
  // uses after Save, so the "always available" list Print action renders
  // identically to the automatic post-save preview.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewData, setPreviewData] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [confirm, ConfirmationDialogComponent] = useConfirmation();

  // Fetch dropdowns for filters
  const fetchDropdowns = useCallback(async () => {
    if (!clientId) return;
    try {
      const [custRes, vehRes, venRes, vgRes] = await Promise.all([
        axiosInstance.get("/api/customers", {
          params: { client_id: clientId },
        }),
        axiosInstance.get("/api/vehicles", { params: { client_id: clientId } }),
        axiosInstance.get("/api/vendors", { params: { client_id: clientId } }),
        axiosInstance.get("/api/components/category", {
          params: { type: "VehicleGroup", client_id: clientId },
        }),
      ]);
      setCustomers(custRes.data || []);
      setVehicles(vehRes.data || []);
      setVendors(venRes.data || []);
      setVehicleGroups(vgRes.data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch filter dropdowns.",
        severity: "error",
      });
    }
  }, [clientId]);

  const groupDutiesByTripsheet = (duties) => {
    const groups = {};

    duties.forEach((duty) => {
      const key = duty.tripsheetid;

      // Convert database value safely to boolean
      const isDeleted =
        duty.is_deleted === true ||
        String(duty.is_deleted).toLowerCase() === "true";

      if (!groups[key]) {
        groups[key] = {
          tripsheetid: duty.tripsheetid,
          trip_duty_id: duty.trip_duty_id,
          tripsheet_number: duty.tripsheet_number,
          open_date: duty.open_date,
          close_date: duty.close_date,
          duty_location_name: duty.duty_location_name,
          customer_name: duty.customer_name,
          open_time: duty.open_time,
          duty: duty.duty,
          vehicle_number: duty.vehicle_number,
          vendor_vehicle_no: duty.vendor_vehicle_no,
          vehicle_group_name: duty.vehicle_group_name,
          used_by_names: duty.used_by_names,
          invoiceid: duty.invoiceid,
          is_vendor_tripsheet: duty.is_vendor_tripsheet,
          vendor_name: duty.vendor_name,
          payment_mode: duty.payment_mode,
          duty_count: 0,
          dutyIds: new Set(),

          // Cancelled status
          is_deleted: isDeleted,

          // Non Closed status
          has_unclosed_duty: false,

          // Closed by Driver status (rates are not auto-calculated)
          needs_manual_rates: false,
          closed_by_driver_at: null,
        };
      } else {
        // If ANY duty belonging to this tripsheet is deleted,
        // the complete tripsheet is treated as Cancelled.
        if (isDeleted) {
          groups[key].is_deleted = true;
        }
      }

      // Count unique duties
      if (!groups[key].dutyIds.has(duty.trip_duty_id)) {
        groups[key].duty_count += 1;
        groups[key].dutyIds.add(duty.trip_duty_id);
      }

      // Cancelled tripsheet must NOT be treated as Non Closed
      if (isDeleted) {
        return;
      }

      /*
       * CLOSED BY DRIVER
       *
       * The driver ended this duty from the driver app and the office has
       * not finalized the rates yet (rate_calc_status = MANUAL_REQUIRED).
       * If ANY duty of the tripsheet is like this, the tripsheet shows it.
       */
      if (
        duty.needs_manual_rates === true ||
        duty.rate_calc_status === "MANUAL_REQUIRED"
      ) {
        groups[key].needs_manual_rates = true;

        const closedAt = duty.closed_by_driver_at;
        if (
          closedAt &&
          (!groups[key].closed_by_driver_at ||
            dayjs(closedAt).isAfter(groups[key].closed_by_driver_at))
        ) {
          groups[key].closed_by_driver_at = closedAt;
        }
      }

      /*
       * NON CLOSED CONDITION
       *
       * Non Closed only when:
       *
       * Close Time = null / empty / 00:00 / 00:00:00 / --:--
       *
       * AND
       *
       * Close KM = null / empty / 0
       */

      const closeTime = String(duty.close_time ?? "").trim();

      const closeKmValue = duty.close_km_reading;

      const closeKm =
        closeKmValue === null ||
        closeKmValue === undefined ||
        String(closeKmValue).trim() === ""
          ? 0
          : Number(closeKmValue);

      const isCloseTimeEmpty =
        closeTime === "" ||
        closeTime === "--:--" ||
        closeTime === "00:00" ||
        closeTime === "00:00:00";

      const isCloseKmEmpty =
        closeKmValue === null ||
        closeKmValue === undefined ||
        String(closeKmValue).trim() === "" ||
        Number.isNaN(closeKm) ||
        closeKm === 0;

      const isDutyUnclosed = isCloseTimeEmpty && isCloseKmEmpty;

      if (isDutyUnclosed) {
        groups[key].has_unclosed_duty = true;
      }
    });

    return Object.values(groups);
  };

  const groupedTripsheets = groupDutiesByTripsheet(tripDuties)
    .filter((row) => {
      if (filterStatus === "All") return true;

      if (filterStatus === "Cancelled") {
        return row.is_deleted === true;
      }

      if (row.is_deleted === true) return false;

      if (filterStatus === "Closed by Driver") {
        return row.needs_manual_rates;
      }

      if (row.needs_manual_rates) return false;

      if (filterStatus === "Non Closed") {
        return row.has_unclosed_duty;
      }

      if (row.has_unclosed_duty) return false;

      if (filterStatus === "Non Billed") {
        return !row.invoiceid || Number(row.invoiceid) === 0;
      }

      if (filterStatus === "Billed") {
        return !!row.invoiceid && Number(row.invoiceid) !== 0;
      }

      return true;
    })
    .sort((a, b) => {
      const numA = parseInt(
        (a.tripsheet_number || "").split("-")[0].replace(/[^\d]/g, ""),
        10,
      );
      const numB = parseInt(
        (b.tripsheet_number || "").split("-")[0].replace(/[^\d]/g, ""),
        10,
      );

      return numB - numA;
    });

  const pagedRows = groupedTripsheets.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Fetch trip duties with filters
   const fetchTripDuties = useCallback(async () => {
    if (!clientId || !fyid) return;
    try {
      const params = new URLSearchParams();
      params.append("client_id", clientId);
      params.append("fyid", fyid);
      // "Closed by Driver" is not an API status: load everything and
      // filter in the browser (see groupedTripsheets above)
      if (filterStatus !== "All" && filterStatus !== CLIENT_SIDE_STATUS)
        params.append("status", filterStatus.toLowerCase().replace(" ", "_"));
      if (filterTripsheetNumber)
        params.append("tripsheet_number", filterTripsheetNumber);
      if (filterFromDate)
        params.append("from_date", dayjs(filterFromDate).format("YYYY-MM-DD"));
      if (filterToDate)
        params.append("to_date", dayjs(filterToDate).format("YYYY-MM-DD"));
      
    if (filterCustomer) {params.append("customer_id", String(filterCustomer));
}

      if (filterVehicle) params.append("vehicle_id", filterVehicle);
      if (filterVendor) params.append("vendor_id", filterVendor);
      if (filterVehicleGroup)
        params.append("vehicle_group_id", filterVehicleGroup);

      const res = await axiosInstance.get(
        `/api/tripsheets/duties?${params.toString()}`,
      );
      setTripDuties(res.data);
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch tripsheet records.",
        severity: "error",
      });
    }
  }, [
    clientId,
      fyid,
    filterStatus,
    filterTripsheetNumber,
    filterFromDate,
    filterToDate,
    filterCustomer,
    filterVehicle,
    filterVendor,
    filterVehicleGroup,
  ]);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  useEffect(() => {
    fetchTripDuties();
  }, [fetchTripDuties]);

  // Back to the first page when the status filter changes
  useEffect(() => {
    setPage(0);
  }, [filterStatus]);

  // Pagination handlers
  const handleChangePage = (_event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Menu handlers
  const handleRowMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const handleRowMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  // Delete handler
  const handleDelete = async (row) => {
    handleRowMenuClose();

    if (!row?.tripsheetid) {
      setSnackbar({
        open: true,
        msg: "Tripsheet ID not found",
        severity: "error",
      });
      return;
    }

    if (
      !(await confirm(
        `Are you sure you want to cancel tripsheet ${row.tripsheet_number}?`,
      ))
    ) {
      return;
    }

    try {
      await axiosInstance.delete(
        `/api/tripsheets/${row.tripsheetid}?client_id=${clientId}`,
      );

      setSnackbar({
        open: true,
        msg: "Tripsheet cancelled successfully",
        severity: "success",
      });

      await fetchTripDuties();
    } catch (err) {
      console.error("Delete failed:", err);

      setSnackbar({
        open: true,
        msg:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Delete failed",
        severity: "error",
      });
    }
  };

  const handleEdit = async (row) => {
    try {
      // Use tripsheetid from row, not trip_duty_id
      const res = await axiosInstance.get(
        `/api/tripsheets/${row.tripsheetid}`,
        {
          params: { client_id: clientId },
        },
      );
      console.log("Edit data fetched:", res.data); // Log the tripsheet object
      setEditData(res.data); // Set full tripsheet for editing
      setShowForm(true); // Open form
    } catch (err) {
      setSnackbar({
        open: true,
        msg: "Failed to fetch tripsheet details",
        severity: "error",
      });
    }
    handleRowMenuClose();
  };

  // Office confirms the KM / time and the manually entered rates of a
  // tripsheet that was closed by the driver.
  const handleFinalizeRates = async (row) => {
    handleRowMenuClose();

    if (!row?.tripsheetid) return;

    if (
      !(await confirm(
        `Mark rates as finalized for tripsheet ${row.tripsheet_number}? ` +
          `Please confirm the KM / time are checked and the rates are entered.`,
      ))
    ) {
      return;
    }

    try {
      await axiosInstance.post(
        `/api/tripsheets/${row.tripsheetid}/rates-finalized`,
        { client_id: clientId },
      );

      setSnackbar({
        open: true,
        msg: "Rates marked as finalized",
        severity: "success",
      });

      await fetchTripDuties();
    } catch (err) {
      console.error("Finalize rates failed:", err);

      setSnackbar({
        open: true,
        msg:
          err.response?.data?.error ||
          err.response?.data?.message ||
          "Unable to mark rates as finalized",
        severity: "error",
      });
    }
  };

  // Print handler — always available, regardless of status (Billed/Non Billed/
  // Cancelled/etc). Mirrors exactly what TripsheetFormAdd does after Save:
  //   1) load tripsheet-settings (for template_id / print flags)
  //   2) fetch the full tripsheet (open/close details, duties, passengers)
  //   3) POST to /tripsheet/preview to get the rendered HTML
  //   4) hand that HTML to the shared <PreviewModal> component
  const handlePrint = async (row) => {
    handleRowMenuClose();
    if (!row?.tripsheetid) return;
    setPrintingId(row.tripsheetid);
    try {
      // 1) Tripsheet print settings (template_id, print flags) — same
      // endpoint TripsheetFormAdd loads on mount.
      let tripsheetSettings = {
        template_id: null,
        print_client_name_address_vendor_vehicle: false,
      };
      try {
        const settingsRes = await axiosInstance.get(
          `/api/clients/${clientId}/tripsheet-settings`,
        );
        const settings = settingsRes?.data?.tripsheet;
        tripsheetSettings = {
          template_id: settings?.template_id ?? null,
          print_client_name_address_vendor_vehicle:
            settings?.print_client_name_address_vendor_vehicle === true,
        };
      } catch (settingsErr) {
        console.warn(
          "Could not load tripsheet-settings, using defaults:",
          settingsErr,
        );
      }

      // 2) Full tripsheet detail — same endpoint/shape used by handleEdit
      const detailRes = await axiosInstance.get(
        `/api/tripsheets/${row.tripsheetid}`,
        { params: { client_id: clientId } },
      );
      const data = detailRes.data || {};
      const tripsheetObj = data.tripsheet || data;

      const previewPayload = {
        tripsheet: {
          ...tripsheetObj,
          tripsheetid: row.tripsheetid,
          tripsheet_number:
            row.tripsheet_number || tripsheetObj.tripsheet_number,
          client_id: clientId,
        },
        trip_duties: data.trip_duties || data.duties || [],
        passengers: data.passengers || [],
        itineraries: data.itineraries || [],
        template_id: tripsheetSettings.template_id,
        print_client_name_address_vendor_vehicle:
          tripsheetSettings.print_client_name_address_vendor_vehicle,
      };

      // 3) Render the tripsheet template into HTML
      const previewRes = await axiosInstance.post(
        "/api/document-template/tripsheet/preview",
        previewPayload,
      );

      const html = previewRes?.data?.html || "";
      if (!html) {
        throw new Error("Tripsheet preview HTML was not generated");
      }

      // 4) Hand off to the shared PreviewModal
      setPreviewData(previewPayload);
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (err) {
      console.error("Print failed:", err);
      setSnackbar({
        open: true,
        msg:
          "Failed to generate tripsheet for printing: " +
          (err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "Unknown error"),
        severity: "error",
      });
    } finally {
      setPrintingId(null);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setFilterStatus("All");
    setFilterTripsheetNumber("");
    setFilterFromDate(null);
    setFilterToDate(null);
    setFilterCustomer("");
    setFilterVehicle("");
    setFilterVendor("");
    setFilterVehicleGroup("");
  };

  const compactStyle = {
    minWidth: 140,
    height: 36,
    "& .MuiInputBase-root": {
      height: 36,
      fontSize: "0.75rem",
      padding: "2px 8px",
    },
  };

  const compactSelectStyle = {
    ...compactStyle,
    minWidth: 150,
  };

  const FILTER_HEIGHT = 28;
  const FILTER_FONT = "0.7rem";

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 100px)",
        p: 1,
        bgcolor: "#f8fafc",
      }}
    >
      {!showForm && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            mb: 1,
            alignItems: "center",
            py: 0,
            px: 0,
            background: "#fff",
          }}
        >
          {/* Status */}
          <FormControl
            size="small"
            sx={{ minWidth: 135, height: FILTER_HEIGHT }}
          >
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Status
            </InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "2px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              {STATUS_FILTERS.map((st) => (
                <MenuItem key={st} value={st} sx={{ fontSize: FILTER_FONT }}>
                  {st}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Tripsheet No */}

          <TextField
            size="small"
            label="Tripsheet No"
            value={filterTripsheetNumber}
            onChange={(e) => setFilterTripsheetNumber(e.target.value)}
            sx={{
              minWidth: 120,
              "& .MuiInputBase-root": {
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                minHeight: FILTER_HEIGHT,
                boxSizing: "border-box",
                padding: 0,
                lineHeight: `${FILTER_HEIGHT}px`,
              },
              "& .MuiInputBase-input": {
                height: FILTER_HEIGHT,
                minHeight: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                lineHeight: `${FILTER_HEIGHT}px`, // <-- critical for vertical centering
                padding: "0 8px", // minimal vertical padding
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
              },
              "& .MuiInputLabel-root": {
                fontSize: FILTER_FONT,
                lineHeight: 1,
                top: -6, // adjust as needed
              },
              "& .MuiInputLabel-shrink": {
                top: 0,
                lineHeight: 1.2,
              },
            }}
            InputLabelProps={{ sx: { fontSize: FILTER_FONT } }}
          />

          {/* Customer */}
          <FormControl
            size="small"
            sx={{ minWidth: 145, height: FILTER_HEIGHT }}
          >
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Customer
            </InputLabel>
            <Select
              value={filterCustomer}
              label="Customer"
              onChange={(e) => setFilterCustomer(e.target.value)}
              sx={{
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "2px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              <MenuItem value="" sx={{ fontSize: FILTER_FONT }}>
                All
              </MenuItem>
              {customers.map((c) => (
                <MenuItem
                  key={c.customerid}
                  value={String(c.customerid)}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {c.customername}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vehicle */}
          <FormControl
            size="small"
            sx={{ minWidth: 140, height: FILTER_HEIGHT }}
          >
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Vehicle
            </InputLabel>
            <Select
              value={filterVehicle}
              label="Vehicle"
              onChange={(e) => setFilterVehicle(e.target.value)}
              sx={{
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "2px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              <MenuItem value="" sx={{ fontSize: FILTER_FONT }}>
                All
              </MenuItem>
              {vehicles.map((v) => (
                <MenuItem
                  key={v.vehicle_id}
                  value={v.vehicle_id}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {v.vehicle_no || v.vehicle_number}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vendor */}
          <FormControl
            size="small"
            sx={{ minWidth: 125, height: FILTER_HEIGHT }}
          >
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Vendor
            </InputLabel>
            <Select
              value={filterVendor}
              label="Vendor"
              onChange={(e) => setFilterVendor(e.target.value)}
              sx={{
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "2px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              <MenuItem value="" sx={{ fontSize: FILTER_FONT }}>
                All
              </MenuItem>
              {vendors.map((v) => (
                <MenuItem
                  key={v.vendorid}
                  value={v.vendorid}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {v.vendorname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Vehicle Group */}
          <FormControl
            size="small"
            sx={{ minWidth: 120, height: FILTER_HEIGHT }}
          >
            <InputLabel sx={{ fontSize: FILTER_FONT, lineHeight: 1 }}>
              Vehicle Group
            </InputLabel>
            <Select
              value={filterVehicleGroup}
              label="Vehicle Group"
              onChange={(e) => setFilterVehicleGroup(e.target.value)}
              sx={{
                height: FILTER_HEIGHT,
                fontSize: FILTER_FONT,
                ".MuiSelect-select": { padding: "2px 8px" },
              }}
              MenuProps={{ PaperProps: { sx: { fontSize: FILTER_FONT } } }}
            >
              <MenuItem value="" sx={{ fontSize: FILTER_FONT }}>
                All
              </MenuItem>
              {vehicleGroups.map((vg) => (
                <MenuItem
                  key={vg.categoryid}
                  value={vg.categoryid}
                  sx={{ fontSize: FILTER_FONT }}
                >
                  {vg.categoryname}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* From Date */}
          <MyDatePicker
            label="From Date"
            value={filterFromDate}
            onChange={setFilterFromDate}
            inputStyle={{
              fontSize: FILTER_FONT,
              minHeight: FILTER_HEIGHT,
              height: FILTER_HEIGHT,
              padding: "2px 6px",
            }}
            labelStyle={{
              fontSize: FILTER_FONT,
              lineHeight: 1,
              marginBottom: 0,
              top: -13,
              left: 8, // fine tune as needed
            }}
            containerStyle={{
              width: 140,
              minHeight: FILTER_HEIGHT,
              height: FILTER_HEIGHT,
              marginBottom: 0,
            }}
          />

          {/* To Date */}
          <MyDatePicker
            label="To Date"
            value={filterToDate}
            onChange={setFilterToDate}
            inputStyle={{
              fontSize: FILTER_FONT,
              minHeight: FILTER_HEIGHT,
              height: FILTER_HEIGHT,
              padding: "2px 6px",
            }}
            labelStyle={{
              fontSize: FILTER_FONT,
              lineHeight: 1,
              marginBottom: 0,
              top: -13,
              left: 8, // fine tune as needed
            }}
            containerStyle={{
              width: 140,
              minHeight: FILTER_HEIGHT,
              height: FILTER_HEIGHT,
              marginBottom: 0,
            }}
          />

          {/* Reset/Add */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            onClick={resetFilters}
            sx={{
              minWidth: 90,
              fontSize: "0.68rem",
              height: 26,
              padding: "0 4px",
            }}
          >
            RESET FILTER
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => {
              setEditData(null);
              setShowForm(true);
            }}
            sx={{
              minWidth: 60,
              fontSize: "0.7rem",
              height: 26,
              padding: "0 2px",
            }}
          >
            ADD
          </Button>
        </Box>
      )}

      {/* List or Add/Edit Form */}
      {showForm ? (
        <TripsheetFormAdd
          editData={editData}
          onCancel={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            setSnackbar({
              open: true,
              msg: "Tripsheet saved",
              severity: "success",
            });
            fetchTripDuties();
          }}
        />
      ) : (
        <Paper
          elevation={2}
          sx={{ borderRadius: 2, overflowX: "auto" }}
          ref={tableRef}
        >
          <Table size="small" sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5", height: 36 }}>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
                >
                  Tripsheet No
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
                >
                  Open Date
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 90 }}
                >
                  Location
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 180 }}
                >
                  Customer
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 70 }}
                >
                  Rep Time
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
                >
                  Duty Type
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 120 }}
                >
                  Vehicle No
                </TableCell>
                {/* <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
                >
                  Vendor VehNo
                </TableCell> */}
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 100 }}
                >
                  Vehicle Group
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 120 }}
                >
                  Used By
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 80 }}
                >
                  Mode
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    width: 60,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  #Duty
                </TableCell>
                <TableCell
                  sx={{ fontSize: "0.75rem", fontWeight: "bold", width: 120 }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: "0.75rem",
                    width: 80,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.length > 0 ? (
                pagedRows.map((row) => (
                  <TableRow key={row.trip_duty_id} hover>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.tripsheet_number}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.open_date
                        ? dayjs(row.open_date).format("DD/MM/YYYY")
                        : "-"}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.duty_location_name}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.customer_name}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.open_time
                        ? dayjs(row.open_time, "HH:mm:ss").format("HH:mm")
                        : "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.duty}
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "0.75rem", whiteSpace: "pre-line" }}
                    >
                      {row.is_vendor_tripsheet ? (
                        <>
                          {row.vendor_name || "-"}
                          {row.vendor_vehicle_no
                            ? `\n${row.vendor_vehicle_no}`
                            : ""}
                        </>
                      ) : (
                        row.vehicle_number || "-"
                      )}
                    </TableCell>

                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.vehicle_group_name || "-"}
                    </TableCell>
                    {/* <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.used_by_names || "-"}
                    </TableCell> */}
                    <TableCell
                      sx={{ fontSize: "0.75rem", whiteSpace: "pre-line" }}
                    >
                      {Array.isArray(row.used_by_names)
                        ? row.used_by_names.join("\n")
                        : row.used_by_names || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.75rem" }}>
                      {row.payment_mode || "-"}
                    </TableCell>
                    {/* New cell showing Duty Count */}
                    <TableCell
                      sx={{ fontSize: "0.75rem", textAlign: "center" }}
                    >
                      {row.duty_count || 1}
                    </TableCell>
                    <TableCell>
                      {row.is_deleted === true ? (
                        <span
                          style={{
                            backgroundColor: "#d32f2f",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Cancelled
                        </span>
                      ) : row.has_unclosed_duty && !row.needs_manual_rates ? (
                        <span
                          style={{
                            backgroundColor: "#ff9800",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Non Closed
                        </span>
                      ) : row.needs_manual_rates ? (
                        <Tooltip
                          arrow
                          title={
                            `Closed from the driver app` +
                            (row.closed_by_driver_at
                              ? ` on ${dayjs(row.closed_by_driver_at).format(
                                  "DD/MM/YYYY HH:mm",
                                )}`
                              : "") +
                            `. Rates are not calculated automatically for this ` +
                            `tripsheet. Please check the KM / time and enter ` +
                            `the rates manually.`
                          }
                        >
                          <div style={{ cursor: "help" }}>
                            <span
                              style={{
                                backgroundColor: "#5e35b1",
                                color: "#fff",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Closed by Driver
                            </span>
                            <div
                              style={{
                                fontSize: "0.65rem",
                                color: "#5e35b1",
                                marginTop: 2,
                              }}
                            >
                              Rates not auto-calculated
                            </div>
                          </div>
                        </Tooltip>
                      ) : !row.invoiceid || row.invoiceid === 0 ? (
                        <span
                          style={{
                            backgroundColor: "#fbde07ff",
                            color: "#515151ff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Non Billed
                        </span>
                      ) : (
                        <span
                          style={{
                            backgroundColor: "#24a92bff",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontWeight: 500,
                          }}
                        >
                          Billed
                        </span>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleRowMenuOpen(e, row)}
                        title="Actions"
                      >
                        <MoreVertIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={13} align="center">
                    No tripsheet duties to display.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={groupedTripsheets.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `Showing ${from}-${to} of ${count}`
            }
            sx={{
              ".MuiTablePagination-toolbar": { minHeight: 36, height: 36 },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: 12,
                },
            }}
          />
        </Paper>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem onClick={() => handleEdit(menuRow)} sx={{ fontSize: 13 }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handlePrint(menuRow)}
          disabled={printingId === menuRow?.tripsheetid}
          sx={{ fontSize: 13 }}
        >
          <PrintIcon fontSize="small" sx={{ mr: 1 }} />
          {printingId === menuRow?.tripsheetid ? "Preparing..." : "Print"}
        </MenuItem>
        {menuRow?.needs_manual_rates && (
          <MenuItem
            onClick={() => handleFinalizeRates(menuRow)}
            sx={{ fontSize: 13, color: "#5e35b1" }}
          >
            <FinalizeIcon fontSize="small" sx={{ mr: 1 }} /> Mark Rates
            Finalized
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleDelete(menuRow)}
          sx={{ fontSize: 13, color: "error.main" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontSize: "0.95rem",
            fontWeight: 500,
            borderRadius: "8px",
            boxShadow: "0px 3px 8px rgba(0,0,0,0.25)",
            color: "#fff",
            "& .MuiAlert-icon": { color: "#fff" },
            ...(snackbar.severity === "error" && {
              backgroundColor: "#fc0707ff",
            }),
            ...(snackbar.severity === "success" && {
              backgroundColor: "#04710cff",
            }),
          }}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <ConfirmationDialogComponent />

      {/* Print Preview — same shared modal TripsheetFormAdd uses */}
      <PreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewHtml("");
          setPreviewData(null);
        }}
        html={previewHtml}
        title="Tripsheet Preview"
        invoiceData={previewData}
        mode="normal"
        templateType="tripsheet"
        showSnackbar={({ message, severity }) => {
          setSnackbar({ open: true, msg: message, severity });
        }}
      />
    </Box>
  );
}
